import { type DragEvent, type JSX, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Button, Card, Skeleton, Spinner, Stepper } from '@/components/ui';
import {
  useContactLists,
  usePreviewImport,
  useRunImport,
  type ContactListItem,
  type ImportPreview,
  type ImportResult,
} from '@/hooks/useContactLists';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Full-page Excel/CSV import wizard. Lives at /contacts/import. Three steps:
//   1. Upload — pick file (drag or browse), pick list (or use ?listId=).
//      Auto-fetches preview as soon as a file is chosen.
//   2. Map    — confirm phone column. Other columns become custom variables
//                automatically — that's fine for MVP, we don't need a full
//                column-by-column mapping table yet.
//   3. Review — preview first 7 rows + final confirm. After import the same
//                card flips to a result panel with inserted/skipped/invalid.
// ---------------------------------------------------------------------------

type StepNum = 1 | 2 | 3;

export default function ContactsImportPage(): JSX.Element {
  const { t } = useTranslation('default');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialListID = (() => {
    const raw = params.get('listId');
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  })();

  const lists = useContactLists();

  const [step, setStep] = useState<StepNum>(1);
  const [listID, setListID] = useState<number | null>(initialListID);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [phoneColumn, setPhoneColumn] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const previewMut = usePreviewImport();
  const runMut = useRunImport();

  const list: ContactListItem | null =
    (lists.data?.items ?? []).find((l) => l.id === listID) ?? null;

  const onPickFile = async (f: File) => {
    setError(null);
    setResult(null);
    setFile(f);
    try {
      const p = await previewMut.mutateAsync(f);
      setPreview(p);
      setPhoneColumn(p.guessed_phone);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ci_err_generic'));
      setPreview(null);
    }
  };

  const runImport = async () => {
    if (!file || !preview || !listID) return;
    setError(null);
    try {
      const r = await runMut.mutateAsync({ listID, file, phoneColumn });
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ci_err_generic'));
    }
  };

  const canStep1 = listID !== null && preview !== null;
  const canStep2 = phoneColumn !== '';

  return (
    <div className="px-8 py-8 max-w-[960px] mx-auto">
      <Link
        to="/contacts"
        className="inline-flex items-center gap-1 text-[12px] text-text-3 hover:text-text mb-3"
      >
        <Icon.chevronLeft width="14" height="14" />
        {t('ci_back_link')}
      </Link>

      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
          {t('ci_title')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1.5">{t('ci_subhead')}</p>
      </header>

      <Card padding="lg" className="mb-5">
        <Stepper
          current={step}
          steps={[t('ci_step_1'), t('ci_step_2'), t('ci_step_3')]}
        />
      </Card>

      {error && (
        <div
          role="alert"
          className="px-3.5 py-3 mb-4 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger"
        >
          {error}
        </div>
      )}

      <Card padding="lg">
        {step === 1 && (
          <Step1Upload
            list={list}
            lists={lists.data?.items ?? []}
            listsLoading={lists.isLoading}
            onChangeList={setListID}
            onPickFile={(f) => void onPickFile(f)}
            previewing={previewMut.isPending}
          />
        )}
        {step === 2 && preview && (
          <Step2Map
            preview={preview}
            phoneColumn={phoneColumn}
            onChangePhoneColumn={setPhoneColumn}
          />
        )}
        {step === 3 && preview && list && (
          <Step3Review
            preview={preview}
            list={list}
            phoneColumn={phoneColumn}
            result={result}
            running={runMut.isPending}
          />
        )}
      </Card>

      {/* Footer actions */}
      <div className="flex items-center justify-between mt-5">
        <div>
          {step > 1 && !result && (
            <Button
              variant="secondary"
              onClick={() => setStep((s) => (s - 1) as StepNum)}
              disabled={runMut.isPending}
            >
              {t('ci_back')}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {!result ? (
            <>
              <Button variant="secondary" onClick={() => navigate('/contacts')}>
                {t('ci_cancel')}
              </Button>
              {step === 1 && (
                <Button onClick={() => setStep(2)} disabled={!canStep1}>
                  {t('ci_next')}
                </Button>
              )}
              {step === 2 && (
                <Button onClick={() => setStep(3)} disabled={!canStep2}>
                  {t('ci_next')}
                </Button>
              )}
              {step === 3 && (
                <Button onClick={() => void runImport()} disabled={runMut.isPending}>
                  {runMut.isPending ? (
                    <>
                      <Spinner />
                      <span>{t('ci_importing')}</span>
                    </>
                  ) : (
                    t('ci_run_import')
                  )}
                </Button>
              )}
            </>
          ) : (
            <Button onClick={() => navigate('/contacts')}>{t('ci_done')}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Upload + list picker
// ---------------------------------------------------------------------------
interface Step1Props {
  list: ContactListItem | null;
  lists: ContactListItem[];
  listsLoading: boolean;
  onChangeList: (id: number | null) => void;
  onPickFile: (f: File) => void;
  previewing: boolean;
}

function Step1Upload({
  list,
  lists,
  listsLoading,
  onChangeList,
  onPickFile,
  previewing,
}: Step1Props): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onPickFile(f);
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-medium text-text-2 mb-1.5">
          {t('ci_field_list')}
        </label>
        <select
          value={list?.id ?? ''}
          onChange={(e) => onChangeList(e.target.value ? Number(e.target.value) : null)}
          disabled={listsLoading}
          className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
        >
          <option value="">{t('ci_choose_list')}</option>
          {lists.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} ({l.contact_count.toLocaleString(lang)})
            </option>
          ))}
        </select>
        <p className="text-[12px] text-text-4 mt-1">{t('ci_field_list_help')}</p>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-text-2 mb-1.5">
          {t('ci_field_file')}
        </label>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          className={cn(
            'cursor-pointer rounded-md border-2 border-dashed py-12 px-6 text-center transition-colors',
            dragOver ? 'border-volt bg-volt-50' : 'border-line-strong bg-surface-2 hover:bg-surface',
          )}
        >
          <span
            aria-hidden="true"
            className="inline-flex w-14 h-14 rounded-full bg-volt-50 text-volt-600 grid place-items-center mb-3"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div className="text-[14px] text-text font-medium mb-1">
            {previewing ? t('ci_reading_file') : t('ci_drop_or_browse')}
          </div>
          <div className="text-[12px] text-text-3">{t('ci_accepts')}</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.xlsm"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPickFile(f);
            }}
            className="hidden"
          />
        </div>
      </div>

      {previewing && <Skeleton className="h-12 w-full" />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Map columns. Currently a single mapping (phone column);
// other headers become custom variables on contacts.
// ---------------------------------------------------------------------------
interface Step2Props {
  preview: ImportPreview;
  phoneColumn: string;
  onChangePhoneColumn: (v: string) => void;
}

function Step2Map({ preview, phoneColumn, onChangePhoneColumn }: Step2Props): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  return (
    <div>
      <div className="px-3.5 py-3 mb-5 bg-info-bg border border-info-bd rounded-md text-[13px]">
        <strong className="text-info font-semibold">{preview.detected_kind.toUpperCase()}</strong>
        <span className="text-text-2 ml-1.5">
          {t('ci_detected_summary', {
            total: preview.total_rows.toLocaleString(lang),
            cols: preview.headers.length.toLocaleString(lang),
          })}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
              <th className="px-4 py-2.5 font-medium">{t('ci_col_file_column')}</th>
              <th className="px-4 py-2.5 font-medium">{t('ci_col_sample')}</th>
              <th className="px-4 py-2.5 font-medium" />
              <th className="px-4 py-2.5 font-medium">{t('ci_col_target')}</th>
            </tr>
          </thead>
          <tbody>
            {preview.headers.map((h) => {
              const isPhone = h === phoneColumn;
              const sample = preview.rows[0]?.[preview.headers.indexOf(h)] ?? '';
              return (
                <tr key={h} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 mono text-[12px] text-text">{h}</td>
                  <td className="px-4 py-3 text-text-3 text-[12px] truncate max-w-[180px]">
                    {sample}
                  </td>
                  <td className="px-4 py-3 text-text-4">
                    <Icon.arrowRight width="14" height="14" />
                  </td>
                  <td className="px-4 py-3">
                    {isPhone ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-text font-medium">{t('ci_target_phone')}</span>
                        <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.04em] rounded-pill bg-volt-100 text-volt-700 border border-volt-200">
                          {t('ci_target_required')}
                        </span>
                      </span>
                    ) : (
                      <span className="text-text-3">
                        {t('ci_target_variable', { name: h })}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5">
        <label className="block text-[12px] font-medium text-text-2 mb-1.5">
          {t('ci_phone_column_label')}
        </label>
        <select
          value={phoneColumn}
          onChange={(e) => onChangePhoneColumn(e.target.value)}
          className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
        >
          {preview.headers.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <p className="text-[12px] text-text-4 mt-1">{t('ci_phone_column_help')}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Review + import. Flips to result panel after the run.
// ---------------------------------------------------------------------------
interface Step3Props {
  preview: ImportPreview;
  list: ContactListItem;
  phoneColumn: string;
  result: ImportResult | null;
  running: boolean;
}

function Step3Review({ preview, list, phoneColumn, result, running }: Step3Props): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  if (result) {
    return (
      <div className="text-center py-8">
        <span
          aria-hidden="true"
          className="inline-flex w-16 h-16 rounded-full bg-volt-50 grid place-items-center mb-4"
        >
          <span className="w-12 h-12 rounded-full bg-volt grid place-items-center text-volt-ink">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </span>
        <h2 className="display text-[20px] font-semibold text-text mb-2">
          {t('ci_done_title')}
        </h2>
        <p className="text-[13px] text-text-3 max-w-[480px] mx-auto mb-5">
          {t('ci_done_sub', {
            inserted: result.inserted.toLocaleString(lang),
            list: list.name,
          })}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-[440px] mx-auto">
          <ResultKpi label={t('ci_kpi_inserted')} value={result.inserted} tone="success" lang={lang} />
          <ResultKpi label={t('ci_kpi_skipped')} value={result.skipped} tone="warn" lang={lang} />
          <ResultKpi label={t('ci_kpi_invalid')} value={result.invalid} tone="danger" lang={lang} />
        </div>
      </div>
    );
  }

  // Pre-import review.
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <SummaryStat label={t('ci_summary_list')} value={list.name} />
        <SummaryStat
          label={t('ci_summary_total')}
          value={preview.total_rows.toLocaleString(lang)}
          mono
        />
        <SummaryStat label={t('ci_summary_phone_col')} value={phoneColumn} mono />
      </div>

      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
        {t('ci_preview_title')}
      </div>
      <div className="border border-line rounded-md overflow-x-auto max-h-[280px]">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line sticky top-0">
              {preview.headers.map((h) => (
                <th
                  key={h}
                  className={cn(
                    'px-3 py-2 font-medium whitespace-nowrap',
                    h === phoneColumn && 'text-volt-600',
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, i) => (
              <tr key={i} className="border-b border-line last:border-0">
                {preview.headers.map((_h, j) => (
                  <td key={j} className="px-3 py-1.5 text-text-2 whitespace-nowrap">
                    {row[j] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[12px] text-text-3 mt-3">
        {running ? t('ci_running_note') : t('ci_review_note')}
      </p>
    </div>
  );
}

interface SummaryStatProps {
  label: string;
  value: string;
  mono?: boolean;
}

function SummaryStat({ label, value, mono }: SummaryStatProps): JSX.Element {
  return (
    <div className="px-3.5 py-2.5 bg-surface-2 border border-line rounded-md">
      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">{label}</div>
      <div className={cn('text-[14px] text-text font-medium mt-1 truncate', mono && 'mono')}>
        {value}
      </div>
    </div>
  );
}

interface ResultKpiProps {
  label: string;
  value: number;
  tone: 'success' | 'warn' | 'danger';
  lang: string;
}

function ResultKpi({ label, value, tone, lang }: ResultKpiProps): JSX.Element {
  const cls =
    tone === 'success' ? 'text-success' : tone === 'warn' ? 'text-warn' : 'text-danger';
  // Show even when zero — operators want to see "0 invalid" reassurance.
  return (
    <Card padding="md" className="text-center">
      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">{label}</div>
      <div
        className={cn('num display text-[24px] font-semibold mt-1.5 leading-none tracking-tight', cls)}
      >
        {value.toLocaleString(lang)}
      </div>
    </Card>
  );
}

